import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getManagedUserProfile, getProfilesPaginated } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Search, User, X } from "lucide-react";

interface UserSearchSelectProps {
    value?: string;
    onSelect: (userId: string) => void;
    placeholder?: string;
}

export function UserSearchSelect({ value, onSelect, placeholder = "Name oder E-Mail suchen..." }: UserSearchSelectProps) {
    const [searchTerm, setSearchTerm] = useState("");

    const { data: results, isLoading } = useQuery({
        queryKey: ["user-search", searchTerm],
        queryFn: () => getProfilesPaginated({
            roles: ["member", "trainer", "admin"],
            searchTerm,
            roleFilter: "all",
            statusFilter: "all",
            sortKey: "name",
            sortDirection: "asc",
            page: 1,
            pageSize: 5
        }),
        enabled: searchTerm.length >= 2,
    });

    const { data: selectedProfile, isLoading: isSelectedProfileLoading } = useQuery({
        queryKey: ["managed-user-profile", value],
        queryFn: () => getManagedUserProfile(value!),
        enabled: !!value,
    });

    const selectedUser = results?.items.find(u => u.id === value) ?? selectedProfile;
    const selectedUserName = [selectedUser?.first_name, selectedUser?.last_name]
        .filter(Boolean)
        .join(" ")
        || selectedUser?.full_name
        || selectedUser?.email
        || "Ausgewählter Benutzer";

    return (
        <div className="space-y-2">
            {!value ? (
                <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder={placeholder}
                        className="pl-8"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {isLoading && (
                        <div className="absolute right-2 top-2.5">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        </div>
                    )}
                    {results && results.items.length > 0 && searchTerm.length >= 2 && (
                        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-1 shadow-md">
                            {results.items.map((user) => (
                                <button
                                    key={user.id}
                                    type="button"
                                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                                    onClick={() => {
                                        onSelect(user.id);
                                        setSearchTerm("");
                                    }}
                                >
                                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                        <User className="h-3.3 w-3.3 text-primary" />
                                    </div>
                                    <div className="flex flex-col items-start overflow-hidden">
                                        <span className="font-medium truncate w-full">{user.first_name} {user.last_name}</span>
                                        <span className="text-[10px] text-muted-foreground truncate w-full">{user.email}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                    {results && results.items.length === 0 && searchTerm.length >= 2 && !isLoading && (
                        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-2 text-sm text-muted-foreground shadow-md">
                            Keine Benutzer gefunden.
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex items-center justify-between rounded-lg border p-2 bg-muted/30">
                    <div className="flex items-center gap-3 overflow-hidden">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <User className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex flex-col overflow-hidden text-sm">
                            <span className="font-medium truncate">
                                {isSelectedProfileLoading ? "Lade Benutzer..." : selectedUserName}
                            </span>
                            {selectedUser?.email && <span className="text-xs text-muted-foreground truncate">{selectedUser.email}</span>}
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onSelect("")}
                        className="h-8 w-8 p-0"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            )}
        </div>
    );
}
